import { memo, useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import type { GameContent } from "@bastion/data";
import type { ReplayPlayer } from "@bastion/game-core";
import { createReplayHudState } from "./replayHud";
import { useI18n } from "./i18n";

interface ReplayControlsProps {
  player: ReplayPlayer;
  content: GameContent;
  playing: boolean;
  playbackSpeed: 1 | 2 | 4;
  canvasHostRef: RefObject<HTMLDivElement>;
  onPlayingChange: (playing: boolean) => void;
  onAudioUnlock: () => Promise<boolean>;
  onPlaybackSpeedChange: (speed: 1 | 2 | 4) => void;
  onSeek: (seconds: number) => number;
  onExit: () => void;
}

export const ReplayControls = memo(function ReplayControls({
  player,
  content,
  playing,
  playbackSpeed,
  canvasHostRef,
  onPlayingChange,
  onAudioUnlock,
  onPlaybackSpeedChange,
  onSeek,
  onExit,
}: ReplayControlsProps) {
  const { t, tEntity } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [displayTime, setDisplayTime] = useState(player.currentTime);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const replayState = player.state;
  const replayHud = createReplayHudState(
    replayState,
    content,
    player.getEngine().time,
    player.getEngine().isDebugMode,
  );

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
    return () => {
      if (dialog?.open) dialog.close();
    };
  }, []);

  useEffect(() => {
    if (!playing) {
      setDisplayTime(player.currentTime);
      return;
    }
    const updateProgress = () => {
      setDisplayTime(player.currentTime);
    };
    updateProgress();
    const intervalId = window.setInterval(updateProgress, 33);
    return () => window.clearInterval(intervalId);
  }, [player, playing]);

  const seek = (seconds: number) => {
    onPlayingChange(false);
    setDisplayTime(onSeek(seconds));
  };
  const togglePlayback = () => {
    if (playing) {
      onPlayingChange(false);
      return;
    }
    const unlockPromise = onAudioUnlock();
    void unlockPromise.then((unlocked) => {
      setAudioBlocked(!unlocked);
      if (unlocked) onPlayingChange(true);
    });
  };

  return (
    <dialog
      ref={dialogRef}
      className="replay-dialog"
      data-testid="replay-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="replay-dialog-accessible-title"
      onCancel={(event) => {
        event.preventDefault();
        onExit();
      }}
    >
      <section className="replay-controls" data-testid="replay-controls">
        <h1 id="replay-dialog-accessible-title" className="sr-only">
          {t("replay_title")}
        </h1>
        <button
          className="replay-close-btn"
          data-testid="replay-close-btn"
          type="button"
          aria-label={t("replay_exit")}
          onClick={onExit}
        >
          ×
        </button>
        <div className="replay-player-stats" data-testid="replay-player-stats">
          <div className="replay-stat" data-testid="replay-stat-gold">
            <span>{t("hud_gold")}</span>
            <strong>🪙 {replayState.gold.toLocaleString()}</strong>
          </div>
          <div className="replay-stat" data-testid="replay-stat-lives">
            <span>{t("hud_lives")}</span>
            <strong>
              {replayState.baseHp}/{replayState.maxHp}
            </strong>
          </div>
          <div className="replay-stat" data-testid="replay-stat-wave">
            <span>{t("hud_wave")}</span>
            <strong>
              {replayState.wave}/{replayHud.isEndless ? "∞" : replayState.totalWaves}
            </strong>
          </div>
          <div className="replay-stat" data-testid="replay-stat-enemies">
            <span>{t("hud_wave_enemies_count")}</span>
            <strong>
              {replayHud.livingEnemies}/{replayState.enemies.length}
            </strong>
          </div>
        </div>
        <div className="replay-player-main">
          <div className="replay-video-column">
            <div
              className="replay-canvas-host"
              ref={canvasHostRef}
              data-testid="replay-canvas-host"
            />
            {audioBlocked && (
              <p className="replay-audio-warning" role="alert" data-testid="replay-audio-warning">
                {t("replay_audio_blocked")}
              </p>
            )}
            <div className="replay-transport">
              <button
                data-testid="replay-play-btn"
                type="button"
                onClick={togglePlayback}
                disabled={player.isComplete && !playing}
              >
                {playing ? t("replay_pause") : t("replay_play")}
              </button>
              <label>
                {t("replay_speed")}
                <select
                  data-testid="replay-speed-select"
                  value={playbackSpeed}
                  onChange={(event) =>
                    onPlaybackSpeedChange(Number(event.target.value) as 1 | 2 | 4)
                  }
                >
                  {[1, 2, 4].map((speed) => (
                    <option key={speed} value={speed}>
                      {speed}×
                    </option>
                  ))}
                </select>
              </label>
              <button data-testid="replay-exit-btn" type="button" onClick={onExit}>
                {t("replay_exit")}
              </button>
            </div>
            <label
              className="replay-timeline-label"
              data-current-time={displayTime}
              data-total-time={player.durationSeconds}
            >
              {t("replay_time", {
                current: displayTime.toFixed(2),
                total: player.durationSeconds.toFixed(2),
              })}
              <input
                data-testid="replay-timeline"
                type="range"
                min={0}
                max={player.durationSeconds || 0}
                step="any"
                value={displayTime}
                onChange={(event) => seek(Number(event.target.value))}
              />
            </label>
            {player.isComplete && player.resultMatches !== null && (
              <p data-testid="replay-outcome-match">
                {player.resultMatches ? t("replay_result_match") : t("replay_result_mismatch")}
              </p>
            )}
          </div>
          <aside className="replay-skill-rail" data-testid="replay-skill-rail">
            <h2>{t("skill_panel_title")}</h2>
            {replayHud.skills.map((skill) => {
              const progress =
                skill.cooldown > 0 ? 1 - skill.cooldownRemaining / skill.cooldown : 1;
              return (
                <div
                  className="replay-skill-card"
                  data-testid={`replay-skill-${skill.id}`}
                  key={skill.id}
                >
                  <span className="replay-skill-icon" aria-hidden="true">
                    {skill.icon}
                  </span>
                  <span className="replay-skill-name">{tEntity("skill", skill.id)}</span>
                  <span className="replay-skill-cost">🪙 {skill.cost}</span>
                  <span className="replay-skill-cooldown">
                    {skill.ready
                      ? t("skill_ready")
                      : `${t("skill_cd")} ${skill.cooldownRemaining.toFixed(1)}s`}
                  </span>
                  <span className="replay-skill-track" aria-hidden="true">
                    <span style={{ transform: `scaleX(${progress})` }} />
                  </span>
                </div>
              );
            })}
          </aside>
        </div>
      </section>
    </dialog>
  );
});
